import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { Modal, Badge, IconButton, Tooltip } from "@material-ui/core";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { GET_NEWS_ITEMS } from "../Queries/GetNewsItems";
import { ImNewspaper } from "react-icons/im";
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from "react-icons/md";
import { localGetData, localStoreData } from "../utils/utils";

const NewsModal = ({ offline, userId, nightMode }) => {
  const [buttonColor, setButtonColor] = useState("white");
  const [page, setPage] = useState(1);
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [hideBuildNotification, setHideBuildNotification] = useState(true);
  const [dataObj, setDataObj] = useState({});
  const [latestNewsId, setLatestNewsId] = useState(null);
  const lastSeenId = localGetData(`${userId}-last-seen-news-item`) ?? null;

  const [getNewsItems, { loading, error }] = useLazyQuery(GET_NEWS_ITEMS, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: userId, perPage: 1 },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  const fetchNewsPage = async requestedPage => {
    try {
      const response = await getNewsItems({ variables: { page: requestedPage } });
      const newsItem = response?.data?.newsItems?.[0];
      const pageData = {
        id: newsItem?.id ?? null,
        content: newsItem?.content ?? null,
      };

      setDataObj(prevState => {
        if (requestedPage === 1) {
          return { 1: pageData };
        }
        return { ...prevState, [requestedPage]: pageData };
      });

      if (requestedPage === 1) {
        setLatestNewsId(newsItem?.id ?? null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    if (!lastSeenId && !!latestNewsId) {
      localStoreData(latestNewsId, `${userId}-last-seen-news-item`);
    } else if (!!latestNewsId && +latestNewsId > +lastSeenId) {
      localStoreData(latestNewsId, `${userId}-last-seen-news-item`);
    }
    setShowBuildModal(false);
    return;
  };

  const changePage = int => {
    setPage(prevState => {
      if (int > 0 && dataObj[prevState] && !dataObj[prevState]?.content) {
        return prevState;
      }
      return prevState + int > 1 ? prevState + int : 1;
    });
  };

  useEffect(() => {
    if (!!latestNewsId && (!lastSeenId || +latestNewsId > +lastSeenId)) {
      setHideBuildNotification(false);
    } else {
      setHideBuildNotification(true);
    }
  }, [latestNewsId, lastSeenId]);

  useEffect(() => {
    if ((!dataObj[page] || page === 1) && userId) {
      fetchNewsPage(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    setPage(1);
  }, [showBuildModal]);

  const leftArrowColor = nightMode ? (page < 2 ? "" : "white") : "";
  const rightArrowDisabled = loading || (!!dataObj[page] && !dataObj[page]?.content);
  const rightArrowColor = nightMode
    ? rightArrowDisabled
      ? "grey"
      : "white"
    : "";
  return (
    <>
      <Tooltip title="Valcour News" placement="top">
        <IconButton
          onClick={() => {
            setShowBuildModal(!showBuildModal);
          }}
          style={{ color: offline ? "grey" : buttonColor }}
          onMouseEnter={() => setButtonColor("#d9833b")}
          onMouseLeave={() => setButtonColor("white")}
          disabled={offline}
        >
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            color="secondary"
            variant="dot"
            badgeContent=""
            invisible={hideBuildNotification}
            overlap="rectangular"
          >
            <ImNewspaper style={{ fontSize: "2.5vw" }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Modal
        open={showBuildModal}
        onClose={handleClose}
        style={{ ...{ width: "fit-content", margin: "auto" }, ...styles.modal }}
        data-cy="news-modal"
        keepMounted={false}
      >
        <div id={"news-modal-top-div"} style={styles.container}>
          <div
            style={
              nightMode
                ? styles.headerContainerDark
                : styles.headerContainerLight
            }
          >
            <div id="empty-div"></div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <HoverIconButton
                onClick={() => changePage(-1)}
                disabled={page < 2}
              >
                <MdOutlineKeyboardArrowLeft color={leftArrowColor} />
              </HoverIconButton>
              <h2
                style={
                  nightMode ? styles.headerTextDark : styles.headerTextLight
                }
              >
                Valcour News
              </h2>
              <HoverIconButton
                onClick={() => changePage(1)}
                disabled={rightArrowDisabled}
              >
                <MdOutlineKeyboardArrowRight color={rightArrowColor} />
              </HoverIconButton>
            </div>
            <button style={styles.centerButton} onClick={handleClose}>
              X
            </button>
          </div>
          <Content
            modalOpen={showBuildModal}
            content={dataObj[page]?.content}
            loading={loading}
            error={error}
            nightMode={nightMode}
          />
        </div>
      </Modal>
    </>
  );
};

const Content = ({ content, nightMode, loading, error }) => {
  const quillRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const [isQuillReady, setIsQuillReady] = useState(false);
  const [initError, setInitError] = useState(null);

  const styleObj = {
    maxWidth: "75vw",
    maxHeight: "80vh",
    overflow: "auto",
    backgroundColor: nightMode ? "#ccc" : "",
  };

  useEffect(() => {
    let mounted = true;
    const quillElement = quillRef.current;

    const initQuill = () => {
      if (!quillRef.current) return;
      if (quillInstanceRef.current?.root === quillRef.current) return;

      try {
        if (!mounted || !quillRef.current) return;

        quillInstanceRef.current = new Quill(quillRef.current, options);
        setIsQuillReady(true);
        setInitError(null);
      } catch (e) {
        console.error(e);
        if (mounted) {
          setInitError("Unable to load news editor");
        }
      }
    };

    initQuill();

    return () => {
      mounted = false;
      quillInstanceRef.current = null;
      if (quillElement) {
        quillElement.innerHTML = "";
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (isQuillReady && quillInstanceRef.current && (loading || error)) {
      quillInstanceRef.current.setText("");
    } else if (isQuillReady && quillInstanceRef.current && content) {
      try {
        quillInstanceRef.current.setContents(
          JSON.parse(decodeURIComponent(content)),
        );
      } catch (e) {
        console.error(e);
      }
    } else if (isQuillReady && quillInstanceRef.current && !loading && !error) {
      quillInstanceRef.current.setText("No more news");
    }
  }, [content, isQuillReady, loading, error]);

  return (
    <div>
      {loading && <p style={{ padding: "12px 15px", margin: 0 }}>Loading...</p>}
      {error && (
        <p>
          Valcour Mobile encountered an error fetching the news.{" "}
          {JSON.stringify(error)}
        </p>
      )}
      {initError && <p style={{ padding: "12px 15px", margin: 0 }}>{initError}</p>}
      <div
        style={{ ...styleObj, display: loading || error || initError ? "none" : "block" }}
        ref={quillRef}
      />
    </div>
  );
};

const HoverIconButton = props => {
  const [color, setColor] = useState("#303e4f");
  return (
    <IconButton
      style={{ margin: "15px", color: props.disabled ? "grey" : color }}
      onClick={props.onClick}
      disabled={props.disabled}
      onMouseEnter={() => setColor("#d9833b")}
      onMouseLeave={() => setColor("#303e4f")}
    >
      {props.children}
    </IconButton>
  );
};
const styles = {
  container: {
    width: "75vw",
    flexGrow: 1,
    backgroundColor: "white",
    height: "fit-content",
    marginTop: "5vh",
  },
  headerContainerDark: {
    backgroundColor: "#303e4f",
    display: "flex",
    justifyContent: "space-between",
    padding: "0px 10px",
  },
  headerContainerLight: {
    backgroundColor: "#fae8ad",
    display: "flex",
    justifyContent: "space-between",
    padding: "0px 10px",
  },
  headerTextLight: {
    color: "#303e4f",
    fontFamily: "Arial, sans-serif",
  },
  headerTextDark: {
    color: "white",
    fontFamily: "Arial, sans-serif",
  },

  centerButton: {
    margin: "auto 0px",
  },

  modal: {
    display: "flex",
    // alignItems: 'center',
    justifyContent: "center",
  },
};

var options = {
  modules: {
    toolbar: false,
  },
  // placeholder: "Loading News...",
  readOnly: true,
  theme: "snow",
};

export default NewsModal;
